/**
 * jw-product-display.js
 * Phase F2.5.2 — Pricing Display Layer (DOM renderer, zero calculation)
 *
 * 职责：
 *   Render tier cards, discount pill, subtotal to DOM.
 *   Consumes Resolved Facts from JWProductPricing.getResolvedFacts().
 *
 * Fact Boundary:
 *   Display formats what it receives. It does NOT know _why_ a tier
 *   was selected, _why_ a discount percentage is what it is, or _how_
 *   prices were derived. All business facts come from upstream.
 *
 * 依赖：
 *   JWProductPricing.getResolvedFacts()  (Resolved Facts provider)
 *   DOM: #tiers, #mobTiers, #mobTiersWrap, #pcDiscountPill, #mobDiscountPill,
 *        #subtotal, #stickyTotal
 *
 * 🚫 禁止：
 *   - 读取 priceRaw / tierData / _tierData
 *   - 调用 tierPrice() / getTierPrice() / 任何价格解析
 *   - 计算折扣百分比
 *   - 操作 Gallery / Variant / State DOM
 *   - 修改 State (S.activeSku / S.qtys)
 *   - 绑定点击事件（属于 product.liquid 编排层）
 *
 * Contract: docs/contracts/product_display_v1.md
 */
(function () {
  'use strict';

  // ── DOM helpers ──
  function $(sel) { return document.querySelector(sel); }
  function $$(sel) { return document.querySelectorAll(sel); }

  // ═══════════════ Tier Cards ═══════════════

  /**
   * Render Desktop + Mobile tier cards.
   *
   * @param {Array<{min:number, price:number}>} tiers              — Compiler tiers array
   * @param {number}                            activeIdx           — active tier index
   * @param {number}                            compareAtPriceDollars — Marketing Engine MSRP for strike-through
   */
  function renderTierCards(tiers, activeIdx, compareAtPriceDollars) {
    if (!tiers || tiers.length === 0) {
      hideTierContainers();
      return;
    }
    showTierContainers();

    // ── Desktop ──
    var tierContainer = $('#tiers');
    var desktopCards  = $$('#tiers .tier-card');

    if (desktopCards.length === 0 && tierContainer) {
      // Dynamic creation: SSR had no cards
      tierContainer.innerHTML = '';
      tiers.forEach(function(t, idx) {
        var sp = Number(t.price);
        var isLast = idx === tiers.length - 1;
        var label = isLast
          ? ('≥' + t.min + ' pcs')
          : (t.min + '-' + (tiers[idx + 1].min - 1) + ' pcs');

        var strikeHtml = '';
        if (sp < compareAtPriceDollars && compareAtPriceDollars > 0) {
          strikeHtml = '<div class="text-[11px] text-gray-400 line-through">$' + compareAtPriceDollars.toFixed(2) + '</div>';
        } else {
          strikeHtml = idx === 0
            ? '<div class="text-[11px] text-gray-400">Min. Order</div>'
            : '<div class="text-[11px]">&nbsp;</div>';
        }

        var card = document.createElement('div');
        card.className = 'group tier-card flex-1 flex flex-col items-center rounded-lg px-2 py-2.5 cursor-pointer transition bg-gray-50 hover:bg-gray-100';
        card.setAttribute('data-ti', idx);
        card.setAttribute('data-min', t.min);
        card.setAttribute('data-active', idx === activeIdx ? 'true' : 'false');
        card.innerHTML =
          '<div class="tier-lbl text-[11px] text-gray-500">' + label + '</div>' +
          '<div class="tier-pri text-[15px] font-bold text-gray-800">$' + sp.toFixed(2) + '</div>' +
          '<div class="tier-strike-wrap">' + strikeHtml + '</div>';
        tierContainer.appendChild(card);
      });
    } else {
      // SSR cards exist — textContent update only
      desktopCards.forEach(function(card, idx) {
        var t = tiers[idx];
        if (!t) { card.style.display = 'none'; return; }
        card.style.display = '';
        var sp = Number(t.price);

        var pn = card.querySelector('.tier-pri');
        if (pn) pn.textContent = '$' + sp.toFixed(2);

        var lbl = card.querySelector('.tier-lbl');
        if (lbl) {
          var isLast = idx === tiers.length - 1;
          lbl.textContent = isLast
            ? ('≥' + t.min + ' pcs')
            : (t.min + '-' + (tiers[idx + 1].min - 1) + ' pcs');
        }

        var sw = card.querySelector('.tier-strike-wrap');
        if (sw) {
          if (sp < compareAtPriceDollars && compareAtPriceDollars > 0) {
            sw.innerHTML = '<div class="text-[11px] text-gray-400 line-through">$' + compareAtPriceDollars.toFixed(2) + '</div>';
          } else {
            sw.innerHTML = idx === 0
              ? '<div class="text-[11px] text-gray-400">Min. Order</div>'
              : '<div class="text-[11px]">&nbsp;</div>';
          }
        }

        card.setAttribute('data-active', idx === activeIdx ? 'true' : 'false');
        card.setAttribute('data-min', t.min);
      });
    }

    // ── Mobile ──
    var mobContainer = $('#mobTiers');
    var mobileCards  = $$('#mobTiers .mob-tier-card');

    if (mobileCards.length === 0 && mobContainer) {
      // Dynamic creation
      mobContainer.innerHTML = '';
      tiers.forEach(function(t, idx) {
        var sp = Number(t.price);
        var isLast = idx === tiers.length - 1;
        var label = isLast
          ? ('≥' + t.min + ' Pieces')
          : (t.min + '-' + (tiers[idx + 1].min - 1) + ' Pieces');

        var strikeHtml = '';
        if (sp < compareAtPriceDollars && compareAtPriceDollars > 0) {
          strikeHtml = '<div class="text-[11px] text-gray-400 line-through">$' + compareAtPriceDollars.toFixed(2) + '</div>';
        }

        var card = document.createElement('div');
        card.className = 'group mob-tier-card shrink-0 rounded-lg px-3 py-2.5 cursor-pointer transition text-center min-w-[80px] bg-[var(--primary-light)]';
        card.setAttribute('data-mti', idx);
        card.setAttribute('data-min', t.min);
        card.setAttribute('data-active', idx === activeIdx ? 'true' : 'false');
        card.innerHTML =
          '<div class="mob-tier-lbl text-[11px] text-gray-600 font-medium whitespace-nowrap">' + label + '</div>' +
          '<div class="text-sm font-bold text-gray-800 tier-price-num">$' + sp.toFixed(2) + '</div>' +
          '<div class="tier-strike-wrap">' + strikeHtml + '</div>';
        mobContainer.appendChild(card);
      });
    } else {
      mobileCards.forEach(function(card, idx) {
        var t = tiers[idx];
        if (!t) { card.style.display = 'none'; return; }
        card.style.display = '';
        var sp = Number(t.price);

        var pn = card.querySelector('.tier-price-num');
        if (pn) pn.textContent = '$' + sp.toFixed(2);

        var lbl = card.querySelector('.mob-tier-lbl');
        if (lbl) {
          var isLast = idx === tiers.length - 1;
          lbl.textContent = isLast
            ? ('≥' + t.min + ' Pieces')
            : (t.min + '-' + (tiers[idx + 1].min - 1) + ' Pieces');
        }

        var sw = card.querySelector('.tier-strike-wrap');
        if (sw) {
          if (sp < compareAtPriceDollars && compareAtPriceDollars > 0) {
            sw.innerHTML = '<div class="text-[11px] text-gray-400 line-through">$' + compareAtPriceDollars.toFixed(2) + '</div>';
          } else {
            sw.innerHTML = '';
          }
        }

        card.setAttribute('data-active', idx === activeIdx ? 'true' : 'false');
        card.setAttribute('data-min', t.min);
      });
    }
  }

  // ═══════════════ Discount Pill ═══════════════

  /**
   * Render discount pill on Desktop + Mobile.
   * Display does NOT know where this percentage comes from (Marketing Engine).
   *
   * @param {number} discountPercent — integer (e.g. 24), 0 = hide
   */
  function renderDiscountPill(discountPercent) {
    var disc = Number(discountPercent) || 0;
    var pcPill  = $('#pcDiscountPill');
    var mobPill = $('#mobDiscountPill');

    if (pcPill) {
      pcPill.style.display = disc > 0 ? '' : 'none';
      pcPill.textContent = disc > 0 ? disc + '% OFF' : '';
    }
    if (mobPill) {
      mobPill.style.display = disc > 0 ? '' : 'none';
      mobPill.textContent = disc > 0 ? disc + '% OFF' : '';
    }
  }

  // ═══════════════ Subtotal ═══════════════

  /**
   * Render subtotal / sticky total.
   * Display does NOT calcTotal() — it receives the pre-calculated value.
   *
   * @param {number} totalDollars — pre-calculated total in dollars
   */
  function renderSubtotal(totalDollars) {
    var formatted = Number(totalDollars).toFixed(2);
    var st  = $('#subtotal');
    var sty = $('#stickyTotal');
    if (st)  st.textContent  = '$' + formatted;
    if (sty) sty.textContent = '$' + formatted;
  }

  // ═══════════════ Visibility ═══════════════

  function hideTierContainers() {
    var t   = $('#tiers');        if (t)   t.style.display   = 'none';
    var mtw = $('#mobTiersWrap'); if (mtw) mtw.style.display = 'none';
    var mt  = $('#mobTiers');     if (mt)  mt.style.display  = 'none';
  }

  function showTierContainers() {
    var t   = $('#tiers');        if (t)   t.style.display   = '';
    var mtw = $('#mobTiersWrap'); if (mtw) mtw.style.display = '';
    var mt  = $('#mobTiers');     if (mt)  mt.style.display  = '';
  }

  // ── Expose ──
  window.JWProductDisplay = {
    renderTierCards:    renderTierCards,
    renderDiscountPill: renderDiscountPill,
    renderSubtotal:     renderSubtotal
  };

})();
